var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getQuestionSetComponentTypes, updateQuestionSetComponentTypes } from '../services/ComponentTypesService';
// Query Keys
export const QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS = {
    all: ['questionSetComponentTypes'],
    bySet: (questionSetId) => ['questionSetComponentTypes', 'set', questionSetId]
};
// Hook to fetch component types for a specific question set
export function useQuestionSetComponentTypes(questionSetId) {
    return useQuery({
        queryKey: QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(questionSetId),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const response = yield getQuestionSetComponentTypes(questionSetId);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return ((_a = response.data) === null || _a === void 0 ? void 0 : _a.componentTypes.sort((a, b) => a.typeName.localeCompare(b.typeName))) || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
        enabled: !!questionSetId // Only run query if questionSetId is provided
    });
}
// Hook to update component type associations for a question set
export function useUpdateQuestionSetComponentTypesMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ questionSetId, componentTypeIds }) {
            const response = yield updateQuestionSetComponentTypes(questionSetId, componentTypeIds);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ questionSetId, componentTypeIds }) {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({
                queryKey: QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(questionSetId)
            });
            // Snapshot the previous value
            const previousComponentTypes = queryClient.getQueryData(QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(questionSetId));
            // Optimistically update the component types
            if (previousComponentTypes) {
                const optimisticUpdate = previousComponentTypes.map((ct) => (Object.assign(Object.assign({}, ct), { associated: componentTypeIds.includes(ct.id) })));
                queryClient.setQueryData(QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(questionSetId), optimisticUpdate);
            }
            // Return a context object with the snapshotted value
            return { previousComponentTypes, questionSetId };
        }),
        onSuccess: (_data, { questionSetId }) => {
            toast.success('Component types updated successfully!');
            // Invalidate related queries
            void queryClient.invalidateQueries({
                queryKey: QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(questionSetId)
            });
            // Also invalidate the question sets list to update counts
            void queryClient.invalidateQueries({
                queryKey: ['componentQuestionSets']
            });
        },
        onError: (error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if ((context === null || context === void 0 ? void 0 : context.previousComponentTypes) && (context === null || context === void 0 ? void 0 : context.questionSetId)) {
                queryClient.setQueryData(QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(context.questionSetId), context.previousComponentTypes);
            }
            toast.error(error.message || 'Failed to update component types. Please try again.');
        },
        onSettled: (_data, _error, { questionSetId }) => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: QUESTION_SET_COMPONENT_TYPES_QUERY_KEYS.bySet(questionSetId)
            });
        }
    });
}
